import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ClipboardList, Eye } from 'lucide-react';
import { useState } from 'react';

import type { ListAdminRequests } from '@cold-ambulance/client';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@cold-ambulance/shadcn';
import {
  SearchInput,
  TableRowSort,
  formatDate,
  useData,
} from '@cold-ambulance/ui';

import { createdAtColumn } from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { ViewRequestDialog } from './view-request-dialog.tsx';

type RequestRecord = ListAdminRequests['records'][number];

const projectTypeLabels: Record<RequestRecord['projectType'], string> = {
  GOVERNMENT: 'Government',
  PRIVATE: 'Private',
};

const paymentTypeLabels: Record<string, string> = {
  MONTHLY: 'Monthly',
  DELIVERABLES: 'Deliverables',
};

const columns: ColumnDef<RequestRecord>[] = [
  {
    accessorKey: 'projectName',
    header: 'Name',
    cell: ({ row }) => (
      <div className="font-medium">{row.original.projectName}</div>
    ),
  },
  {
    accessorKey: 'projectType',
    header: 'Type',
    enableSorting: false,
    cell: ({ row }) => (
      <Badge variant="outline">
        {projectTypeLabels[row.original.projectType]}
      </Badge>
    ),
  },
  {
    id: 'customerName',
    header: 'Customer',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.customer.name}</div>,
  },
  {
    id: 'offerCount',
    header: 'Offers',
    enableSorting: false,
    cell: ({ row }) => (
      <Badge variant="secondary">{row.original._count.offers}</Badge>
    ),
  },
  {
    id: 'schedule',
    header: 'Schedule',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-sm">
        {formatDate(row.original.scheduleStartDate)} –{' '}
        {formatDate(row.original.scheduleEndDate)}
      </div>
    ),
  },
  {
    accessorKey: 'proposalDeadline',
    header: 'Proposal Deadline',
    cell: ({ row }) => (
      <div className="text-sm">
        {formatDate(row.original.proposalDeadline)}
      </div>
    ),
  },
  {
    id: 'location',
    header: 'Location',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-sm">
        {row.original.location?.name || '—'}
      </div>
    ),
  },
  {
    id: 'paymentType',
    header: 'Payment',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-sm">
        {row.original.paymentSchedule
          ? paymentTypeLabels[row.original.paymentSchedule.paymentType]
          : '—'}
      </div>
    ),
  },
  {
    id: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="text-sm">
        {row.original.vendor?.name || '—'}
      </div>
    ),
  },
  createdAtColumn(),
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <ViewRequestDialog
        requestId={row.original.id}
        trigger={
          <Button variant="ghost" size="sm">
            <Eye className="size-4" />
          </Button>
        }
      />
    ),
  },
];

export default function RequestsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /admin/requests',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      query: searchQuery || undefined,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as
              | 'createdAt'
              | 'updatedAt'
              | 'projectName'
              | 'proposalDeadline')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Requests"
        totalCount={`${dataQuery.data?.totalCount || 0} total requests`}
        icon={ClipboardList}
      />

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}
